import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { EmptyState } from "@/components/shared/empty-state";
import { ReviewStepper } from "@/features/quarterly-reviews/review-stepper";
import { SelfReflectionForm } from "@/features/quarterly-reviews/self-reflection-form";
import { SupervisorFeedbackForm } from "@/features/quarterly-reviews/supervisor-feedback-form";
import { useQuarterlyReview } from "@/hooks/use-quarterly-reviews";
import { useCurrentUser, isAdmin } from "@/hooks/use-current-user";
import { getQuarterLabel } from "@/lib/status-machine";
import type { QuarterlyReview } from "@/types/entities";

function ReviewDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-8 w-96" />
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewMetadata({ review }: { review: QuarterlyReview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Year</p>
          <p className="text-sm font-medium">{review.year}</p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Quarter</p>
          <p className="text-sm font-medium">{getQuarterLabel(review.quarter, review.year)}</p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Created</p>
          <p className="text-sm">{new Date(review.created_at).toLocaleDateString()}</p>
        </div>
        {review.finalized_at && (
          <>
            <Separator />
            <div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Finalized</p>
              <p className="text-sm">
                {new Date(review.finalized_at).toLocaleDateString()}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function getReviewPermissions(
  currentUser: { id: string; role: "employee" | "supervisor" | "admin" } | null,
  review: QuarterlyReview,
) {
  const isEmployee = currentUser?.id === review.employee_id;
  const isSupervisor = currentUser?.id === review.supervisor_id;
  const isAdminUser = isAdmin(currentUser?.role);

  const canEditSelfReflection =
    isEmployee && review.status === "pending_self_reflection";
  const canEditSupervisorFeedback =
    (isSupervisor || isAdminUser) &&
    review.status === "pending_supervisor_feedback";

  return { isEmployee, isSupervisor, isAdminUser, canEditSelfReflection, canEditSupervisorFeedback };
}

export function ReviewDetailPage() {
  const { id } = useParams({ from: "/reviews/$id" });
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { data, isLoading, error } = useQuarterlyReview(id);

  if (isLoading) return <ReviewDetailSkeleton />;

  if (error || !data) {
    return (
      <EmptyState
        title="Quarterly Review not found"
        description={
          error?.message ?? "The quarterly review you are looking for does not exist."
        }
        actionLabel="Back to Reviews"
        onAction={() => void navigate({ to: "/reviews" })}
      />
    );
  }

  const review = data.data;
  const { canEditSelfReflection, canEditSupervisorFeedback } = getReviewPermissions(
    currentUser,
    review,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Link
          to="/reviews"
          className="text-sm text-[hsl(var(--muted-foreground))] hover:underline"
        >
          Quarterly Reviews
        </Link>
        <span className="text-sm text-[hsl(var(--muted-foreground))]">/</span>
        <span className="text-sm">
          {review.year} {getQuarterLabel(review.quarter, review.year)}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-semibold">
          {review.year} {getQuarterLabel(review.quarter, review.year)} Review
        </h1>
        {review.status === "finalized" && (
          <Badge variant="accent">Finalized</Badge>
        )}
      </div>

      <ReviewStepper status={review.status} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <SelfReflectionForm review={review} canEdit={canEditSelfReflection} />
          <SupervisorFeedbackForm
            review={review}
            canEdit={canEditSupervisorFeedback}
          />
        </div>

        <div className="space-y-6">
          <ReviewMetadata review={review} />
        </div>
      </div>
    </div>
  );
}
